import React, { useEffect, useRef, useCallback } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./rich-text-editor.css";

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = "",
  onChange,
  placeholder = "Enter text...",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const isInternalChange = useRef(false);

  // Stable callback ref
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleTextChange = useCallback(() => {
    if (isInternalChange.current) return;
    if (quillRef.current) {
      const html = quillRef.current.root.innerHTML;
      // Convert empty editor to empty string
      const isEmpty =
        html === "<p><br></p>" || html === "<p></p>" || html === "";
      onChangeRef.current?.(isEmpty ? "" : html);
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorRef.current, {
        theme: "snow",
        placeholder,
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
      });

      quillRef.current.on("text-change", handleTextChange);

      // Set initial value
      if (value) {
        isInternalChange.current = true;
        quillRef.current.root.innerHTML = value;
        isInternalChange.current = false;
      }
    }

    return () => {
      if (quillRef.current) {
        quillRef.current.off("text-change", handleTextChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, handleTextChange]);

  // Update content when value changes externally
  useEffect(() => {
    if (quillRef.current && !isInternalChange.current) {
      const currentContent = quillRef.current.root.innerHTML;
      const isEmpty =
        currentContent === "<p><br></p>" ||
        currentContent === "<p></p>" ||
        currentContent === "";

      // Only update if the content is actually different
      if (value !== currentContent && !(isEmpty && value === "")) {
        isInternalChange.current = true;
        quillRef.current.root.innerHTML = value || "";
        isInternalChange.current = false;
      }
    }
  }, [value]);

  return (
    <div ref={containerRef} className="rich-text-editor-container">
      <div ref={editorRef} />
    </div>
  );
};

export default RichTextEditor;
